import React, { useCallback, useEffect, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Form from '@atlaskit/form';
import { Box, Inline, xcss, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { fontFallback } from '@atlaskit/theme/typography';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import isValidEmailAddress from '@atlassian/jira-common-is-valid-email-address';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useDebouncedCallback } from '@atlassian/jira-platform-use-debounce';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import GenericError from '../../common/assets/GenericError.svg';
import { DEBOUNCE_DELAY } from '../../common/constants';
import type { ImportType } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import type { UserMappingMap } from '../../common/types';
import { ErrorScreen } from '../../common/ui/error-screen';
import { StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard } from '../../common/ui/step-progress-bar';
import { StepScreen } from '../../common/ui/step-screen';
import {
	useAdvancedOptions,
	useMappingState,
	useMigrationId,
	useSource,
} from '../../controllers/import-state/selectors';
import { useProductSteps } from '../../controllers/utils';
import { useReadCSVMetadata } from '../../services/read-csv-metadata';
import messages from './messages';
import { UserMappingTable } from './user-mapping-table';

type Props = {
	onNext: () => void;
	onReturn: () => void;
	onCloseAndReturnToUploadStep: () => void;
	isCompact?: boolean;
	importType?: ImportType;
};

export const CSVUserMappingScreen = ({
	onReturn,
	onNext,
	onCloseAndReturnToUploadStep,
	isCompact,
	importType,
}: Props) => {
	const { productSteps, currentStepIndex } = useProductSteps();
	const { formatMessage } = useIntl();
	const [source] = useSource();
	const [migrationId] = useMigrationId();
	const [mappingState, { setMappingState }] = useMappingState();
	const [advancedOptions] = useAdvancedOptions();
	const { isLoading, hasErrorOccurred, readCSVMetadata } = useReadCSVMetadata(source);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [userMapping, setUserMapping] = useState(mappingState?.userMapping || null);
	const { isAdmin, isSiteAdmin } = useTenantContext();
	const onReadCSVUser = useCallback(() => {
		if (migrationId !== null && mappingState?.userMapping === null) {
			readCSVMetadata({ migrationId, mappingState, advancedOptions }).then((resp) => {
				if (!resp) {
					return;
				}

				setMappingState({
					...mappingState,
					userMapping: resp.userMapping,
					issueCount: resp.issueCount,
					preFilledUsersCount: resp.preFilledUserCount,
				});
				setUserMapping(resp.userMapping);

				fireTrackAnalytics(createAnalyticsEvent({}), 'csvUser read', {
					numberOfUsers: Object.keys(resp.userMapping).length,
				});
			});
		}
	}, [
		migrationId,
		mappingState,
		readCSVMetadata,
		advancedOptions,
		setMappingState,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		onReadCSVUser();
	}, [onReadCSVUser]);

	const handleToggle = useCallback(() => {
		if (!mappingState) return;
		setMappingState({
			...mappingState,
			shouldImportUsers: !mappingState.shouldImportUsers,
		});
	}, [mappingState, setMappingState]);

	const [handleEmailChange] = useDebouncedCallback((userValue: string, data: UserMappingMap) => {
		if (mappingState && (userValue === '' || isValidEmailAddress(userValue))) {
			setMappingState({
				...mappingState,
				userMapping: data,
			});
		}
	}, DEBOUNCE_DELAY);

	const handleOnchange = useCallback(
		(userKey: string, userValue: string) => {
			if (userMapping) {
				const data = {
					...userMapping,
					[userKey]: { ...userMapping[userKey], userValue },
				};
				setUserMapping(data);
				// if validate email, save to mappingState
				handleEmailChange(userValue, data);
			}
		},
		[handleEmailChange, userMapping],
	);

	const getContent = useCallback(() => {
		if (hasErrorOccurred) {
			return (
				<>
					<ErrorScreen
						onClick={onReadCSVUser}
						illustration={GenericError}
						header={formatMessage(messages.errorHeading)}
						description={formatMessage(messages.errorDescription)}
						testId="project-create-importer-upload-flow.ui.csv-user-mapping-screen.error-screen"
					/>
					<StepFooterStandard
						alignRight
						isFixedPosition={!isCompact}
						onReturn={onReturn}
						shouldDisableNext
					/>
				</>
			);
		}

		// no users in csv file
		if (!isLoading && userMapping && Object.keys(userMapping).length === 0) {
			return (
				<>
					<SectionMessage title={formatMessage(messages.titleForNoUsers)}>
						{formatMessage(messages.descriptionForNoUsers, {
							ul: (chunks: ReactNode[]) => <StyledUl>{chunks}</StyledUl>,
							li: (chunks: ReactNode[]) => <li>{chunks}</li>,
							strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
						})}
					</SectionMessage>
					<StepFooterStandard
						alignRight
						isFixedPosition={!isCompact}
						onReturn={onReturn}
						onClickNext={onNext}
					/>
				</>
			);
		}

		return (
			<>
				<Inline space="space.050" alignBlock="center" xcss={toggleWrapperStyles}>
					<Toggle
						id="toggle-map-user"
						isChecked={mappingState?.shouldImportUsers}
						onChange={handleToggle}
						isDisabled={isLoading}
					/>
					<Label htmlFor="toggle-map-user">{formatMessage(messages.toggleLabel)}</Label>
				</Inline>
				{mappingState?.shouldImportUsers ? (
					<>
						<Box xcss={descriptionStyles}>{formatMessage(messages.description)}</Box>
						{ff('enable-product-admin-for-x2c-migration_zmlsv') && !isSiteAdmin && isAdmin && (
							<Box paddingBlockEnd="space.100">
								<SectionMessage
									title={formatMessage(messages.newUserNotAllowedToImportBannerHeading)}
								>
									<Box as="p">
										{formatMessage(messages.newUserNotAllowedToImportBannerDescription)}
									</Box>
								</SectionMessage>
							</Box>
						)}
						<Form onSubmit={onNext}>
							{({ formProps }) => (
								<form {...formProps}>
									<UserMappingTable
										isLoading={isLoading}
										users={userMapping}
										importType={importType}
										onChange={handleOnchange}
									/>
									<StepFooterStandard
										alignRight
										isFixedPosition={!isCompact}
										onReturn={onReturn}
										nextButtonType="submit"
										shouldDisableNext={isLoading}
									/>
								</form>
							)}
						</Form>
					</>
				) : (
					<>
						<SectionMessage>{formatMessage(messages.descriptionForNotImportUsers)}</SectionMessage>
						<StepFooterStandard
							alignRight
							isFixedPosition={!isCompact}
							onReturn={onReturn}
							onClickNext={onNext}
						/>
					</>
				)}
			</>
		);
	}, [
		formatMessage,
		handleOnchange,
		handleToggle,
		hasErrorOccurred,
		importType,
		isAdmin,
		isCompact,
		isLoading,
		isSiteAdmin,
		mappingState?.shouldImportUsers,
		onNext,
		onReadCSVUser,
		onReturn,
		userMapping,
	]);

	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.USER_MAPPING} sourceType={SCREEN}>
			<StepScreen
				testId="project-create-importer-upload-flow.ui.csv-user-mapping-screen.step-screen"
				renderProgressBar={
					!isCompact && (
						<ProgressBarStandard
							productSteps={productSteps}
							currentStepIndex={currentStepIndex}
							onClose={onCloseAndReturnToUploadStep}
						/>
					)
				}
				header={formatMessage(messages.heading)}
				maxWidth="674px"
			>
				{getContent()}
				<FireScreenAnalytics />
			</StepScreen>
		</ContextualAnalyticsData>
	);
};

const toggleWrapperStyles = xcss({
	marginTop: 'space.200',
	marginBottom: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Label = styled.label({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.heading.xsmall', fontFallback.heading.xsmall),
});

const descriptionStyles = xcss({
	color: 'color.text.subtle',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	paddingBottom: 'space.400',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledUl = styled.ul({
	paddingInlineStart: token('space.300', '24px'),
	marginTop: token('space.050', '4px'),
});
